import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { X } from 'lucide-react';
import { Menu, MenuItem } from '../ui/Menu';
import { EmptyState } from '../ui/EmptyState';
import { db } from '../db/schema';
import { imageSizes } from '../db/images';
import { DeleteItemDialog } from '../board/DeleteItemDialog';
import { MoveItemsDialog } from '../board/MoveItemsDialog';
import { ImportProgress } from '../import/ImportProgress';
import { PasteDialog } from '../import/PasteDialog';
import { ReimportDialog } from '../import/ReimportDialog';
import { useImportRun, useImportStore } from '../import/importStore';
import { useImport } from '../import/useImport';
import { MainPhoto } from './MainPhoto';
import { Gallery } from './Gallery';
import { Lightbox } from './Lightbox';
import { Meta } from './Meta';
import { SourceLink } from './SourceLink';
import { EditableBlock, EditableLine } from './EditableText';
import { useItemEdits } from './useItemEdits';
import { useItemNav } from './useItemNav';
import { usePhotoIntake } from './usePhotoIntake';
import './Panel.css';

/**
 * Everything about one item, beside the board rather than over it. Clicking
 * another tile swaps this panel's contents in place — no close, no reopen, no
 * animation on the panel itself — which is the whole reason the layout is a
 * column and not a modal.
 */
export function ItemPanel({ base, itemId }: { base: string; itemId: string }) {
  const navigate = useNavigate();
  const picker = useRef<HTMLInputElement>(null);

  const [deleting, setDeleting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [pasting, setPasting] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const [moving, setMoving] = useState(false);

  // An import started from the add-item dialog is still running out here; the
  // dialog that kicked it off has already closed.
  const run = useImportRun(itemId);
  const dismissRun = useImportStore((state) => state.dismiss);
  const { importInto } = useImport();

  const data = useLiveQuery(async () => {
    const item = await db.items.get(itemId);
    const cover = item?.coverImageId;
    return { item, size: cover === undefined ? undefined : (await imageSizes([cover])).get(cover) };
  }, [itemId]);

  const item = data?.item;
  const edits = useItemEdits(itemId);
  const busy = deleting || expanded || pasting || reimporting || moving;
  const intake = usePhotoIntake({ itemId, enabled: !busy });

  // The cover leads, then the gallery: the order they read down the panel is
  // the order the strip, the arrows and the lightbox all move through.
  const photos = useMemo(
    () =>
      item === undefined
        ? []
        : [...(item.coverImageId === undefined ? [] : [item.coverImageId]), ...item.imageIds],
    [item],
  );

  // Derived rather than corrected: a pick that isn't in this item's photos is
  // a pick from the item before it, or one that has since been removed, and
  // either way the answer is the cover. Nothing to reset when the item
  // changes, and nothing to clamp when a photo disappears.
  const shown = picked !== undefined && photos.includes(picked) ? picked : photos[0];
  const at = shown === undefined ? -1 : photos.indexOf(shown);

  const close = useCallback(() => void navigate(base), [navigate, base]);
  const remove = useCallback(() => setDeleting(true), []);

  useItemNav({ base, itemId, enabled: !busy, onClose: close, onDelete: remove });

  function onPicked(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files !== null && files.length > 0) intake.add([...files]);
    // Reset, or picking the same file twice in a row fires nothing.
    event.target.value = '';
  }

  function step(delta: -1 | 1) {
    const next = photos[at + delta];
    if (next !== undefined) setPicked(next);
  }

  if (data === undefined) {
    return <aside className="panel" aria-label="Item details" aria-busy="true" />;
  }

  if (item === undefined) {
    return (
      <aside className="panel" aria-label="Item details">
        <div className="panel__bar">
          <button type="button" className="panel__icon" aria-label="Close item" onClick={close}>
            <X size={18} aria-hidden />
          </button>
        </div>
        <EmptyState
          title="Item Not Found"
          line="It may have been deleted, or the link is out of date."
        />
      </aside>
    );
  }

  return (
    <aside className="panel" aria-label="Item details" {...intake.handlers}>
      <div className="panel__bar">
        <button type="button" className="panel__icon" aria-label="Close item" onClick={close}>
          <X size={18} aria-hidden />
        </button>

        <span className={`panel__saved${edits.saved ? ' panel__saved--on' : ''}`} role="status">
          Saved
        </span>

        <Menu label={`Actions for ${item.title}`}>
          {(closeMenu) => (
            <>
              {/* Only when there's a link to go back to. */}
              {item.sourceUrl !== undefined && (
                <MenuItem
                  disabled={run !== undefined && !run.finished}
                  onSelect={() => {
                    closeMenu();
                    setReimporting(true);
                  }}
                >
                  Re-import From Link
                </MenuItem>
              )}

              <MenuItem
                onSelect={() => {
                  closeMenu();
                  setMoving(true);
                }}
              >
                Move To Room
              </MenuItem>

              <MenuItem
                onSelect={() => {
                  closeMenu();
                  remove();
                }}
              >
                Delete Item
              </MenuItem>
            </>
          )}
        </Menu>
      </div>

      {edits.error !== undefined && (
        <p className="panel__warning" role="alert">
          {edits.error}
        </p>
      )}

      {run !== undefined && (
        <ImportProgress
          run={run}
          onDismiss={() => dismissRun(itemId)}
          onPaste={() => setPasting(true)}
        />
      )}

      <MainPhoto
        imageId={shown}
        frame={data.size}
        position={at + 1}
        count={photos.length}
        onOpen={() => setExpanded(true)}
        onAdd={() => picker.current?.click()}
        onStep={step}
      />

      <EditableLine
        className="panel__title"
        value={edits.value('title', item.title)}
        label="Item title"
        placeholder="Untitled"
        onChange={(next) => edits.set('title', next)}
        onCommit={() => edits.commit('title')}
      />

      <Meta item={item} edits={edits} />

      <SourceLink item={item} edits={edits} />

      <section className="panel__section">
        <h3 className="section-label">Photos</h3>
        <Gallery
          itemId={item.id}
          photos={photos}
          coverImageId={item.coverImageId}
          selected={at}
          onSelect={(index) => setPicked(photos[index])}
          onAdd={() => picker.current?.click()}
        />

        {intake.adding > 0 && (
          <p className="panel__notice" role="status">
            Adding {intake.adding} {intake.adding === 1 ? 'photo' : 'photos'}…
          </p>
        )}

        {intake.error !== undefined && (
          <p className="panel__notice" role="alert">
            {intake.error}
            <button type="button" className="panel__dismiss" onClick={intake.dismissError}>
              Dismiss
            </button>
          </p>
        )}
      </section>

      <section className="panel__section">
        <h3 className="section-label">Description</h3>
        <EditableBlock
          className="panel__description"
          value={edits.value('description', item.description)}
          label="Description"
          placeholder="What the store said about it."
          onChange={(next) => edits.set('description', next)}
          onCommit={() => edits.commit('description')}
        />
      </section>

      {/* The app's thesis, rendered as one design decision: what the store
          said sits on paper, what you wrote sits in its own block. */}
      <section className="panel__section">
        <h3 className="section-label">My Notes</h3>
        <div className="panel__notes">
          <EditableBlock
            className="panel__notes-field"
            value={edits.value('notes', item.notes)}
            label="My notes"
            placeholder="What you want to remember about this one."
            onChange={(next) => edits.set('notes', next)}
            onCommit={() => edits.commit('notes')}
          />
        </div>
      </section>

      {/* One picker for both ways in — the empty cover and the gallery's +. */}
      <input
        ref={picker}
        type="file"
        accept="image/*"
        multiple
        className="panel__picker"
        onChange={onPicked}
        tabIndex={-1}
      />

      {intake.dropping && (
        <div className="panel__drop" aria-hidden="true">
          <span className="panel__drop-label">Drop Photos To Add Them</span>
        </div>
      )}

      {/* One selection, two sizes: arrowing through the lightbox moves the
          frame behind it, so closing it leaves you where you were looking. */}
      {expanded && shown !== undefined && (
        <Lightbox
          imageIds={photos}
          index={at}
          onIndex={(next) => setPicked(photos[next])}
          onClose={() => setExpanded(false)}
        />
      )}

      {deleting && (
        <DeleteItemDialog
          item={item}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            setDeleting(false);
            // Nothing left to report progress about.
            dismissRun(itemId);
            close();
          }}
        />
      )}

      {/* The way past a bot wall: the page, pasted by hand. */}
      {pasting && run !== undefined && (
        <PasteDialog
          url={run.url}
          onClose={() => setPasting(false)}
          onPasted={(html) => {
            setPasting(false);
            void importInto(itemId, run.url, html);
          }}
        />
      )}

      {moving && (
        <MoveItemsDialog
          itemIds={[item.id]}
          fromRoomId={item.roomId}
          onClose={() => setMoving(false)}
          onMoved={(roomId) => {
            setMoving(false);
            // The panel is open on an item that now lives on another board.
            void navigate(`/room/${roomId}`);
          }}
        />
      )}

      {reimporting && item.sourceUrl !== undefined && (
        <ReimportDialog
          item={item}
          url={item.sourceUrl}
          onClose={() => setReimporting(false)}
        />
      )}
    </aside>
  );
}
